import { useSyncExternalStore } from "react";

/**
 * The one viewport breakpoint this app's back-office shell branches on.
 *
 * 1024 matches Tailwind's `lg:` and the rule in
 * `docs/design/design-principles.md`: below it the sidebar is hidden and
 * navigation is a ☰ drawer; at or above it the sidebar is permanent.
 *
 * Reimplemented from `apps/seller/src/shared/hooks/useIsMobile.ts` rather than
 * imported — importing across apps is a hard rule violation
 * (`apps/restaurant/AGENTS.md`).
 */
export const MOBILE_BREAKPOINT = 1024;

/** Deliberately `max-width`, so the query is true for the mobile range. */
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

type MediaQueryHost = {
  matchMedia?: (query: string) => {
    matches: boolean;
    addEventListener: (type: "change", listener: () => void) => void;
    removeEventListener: (type: "change", listener: () => void) => void;
  };
};

function host(): MediaQueryHost | undefined {
  return (globalThis as { window?: MediaQueryHost }).window;
}

/** Current answer. `false` where there is no `matchMedia` (tests, SSR). */
export function readIsMobile(): boolean {
  return host()?.matchMedia?.(MOBILE_MEDIA_QUERY).matches ?? false;
}

/**
 * Subscribes to viewport changes and returns the unsubscribe.
 *
 * Exported separately from the hook because this app has no DOM test
 * environment (vitest runs in `node`, and no jsdom is installed in the
 * workspace): the subscribe/unsubscribe contract is the part that leaks a
 * listener if it is wrong, so it is kept directly testable.
 */
export function subscribeToIsMobile(onChange: () => void): () => void {
  const query = host()?.matchMedia?.(MOBILE_MEDIA_QUERY);
  if (!query) {
    return () => {};
  }
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

/** `true` below 1024px. Re-renders on every crossing of the breakpoint. */
export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribeToIsMobile, readIsMobile, () => false);
}
